import {
  ScaffolderFieldError,
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useEffect, useRef, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import {
  Backdrop,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetDaasEntities } from '../../../hooks/useGetDaasEntities/useGetDaasEntities';
import {
  APPLICATION_DESCRIPTION_INFO,
  APPLICATION_NAME_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import { DaasApplicationSetInputData } from '../types';
import { convertEntityToApplicationSetInput } from '../utils';

interface ApplicationSetEditBasicInfoSectionProps {
  value: {
    applicationSetName: string;
    description: string;
    applicationSetRef?: string;
  };
  onChange: (value: {
    applicationSetName: string;
    description: string;
    applicationSetRef?: string;
  }) => void;
  onApplicationSetEntitySelect: (
    data: Partial<DaasApplicationSetInputData>,
  ) => void;
  isFormAlreadyPopulated?: boolean;
  errors?: string[];
}

const findApplicationSetEntity = (
  entities: Entity[],
  applicationSetRef: string | undefined,
): Entity | null => {
  if (applicationSetRef) {
    return (
      entities.find(e => stringifyEntityRef(e) === applicationSetRef) ?? null
    );
  }
  return null;
};

const ApplicationSetEditBasicInfoSectionInner = ({
  value,
  onChange,
  onApplicationSetEntitySelect,
  isFormAlreadyPopulated = false,
  errors = [],
}: ApplicationSetEditBasicInfoSectionProps) => {
  const classes = useStyles();
  const { value: appSetEntities = [], loading: appSetsLoading } =
    useGetDaasEntities('daas-application-set', false, true);

  const selectedEntity =
    value.applicationSetName || value.applicationSetRef
      ? findApplicationSetEntity(appSetEntities, value.applicationSetRef)
      : null;

  const [inaccessibleName, setInaccessibleName] = useState<string | null>(null);
  const hasAutoPrefilled = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const notAccessible =
    Boolean(inaccessibleName) ||
    (errors.length > 0 && !value.applicationSetName);

  useEffect(() => {
    if (notAccessible) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [notAccessible]);

  useEffect(() => {
    if (
      (!value.applicationSetName && !value.applicationSetRef) ||
      appSetsLoading
    )
      return;
    const entity = findApplicationSetEntity(
      appSetEntities,
      value.applicationSetRef,
    );
    if (!entity) {
      setInaccessibleName(value.applicationSetName);
      return;
    }
    setInaccessibleName(null);
    if (hasAutoPrefilled.current || isFormAlreadyPopulated) return;
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationSetInput(entity);
    onChange({
      applicationSetName:
        fullData.applicationSetName || value.applicationSetName,
      description: fullData.description || '',
      applicationSetRef: stringifyEntityRef(entity),
    });
    onApplicationSetEntitySelect(fullData);
  }, [
    value.applicationSetName,
    value.applicationSetRef,
    appSetEntities,
    appSetsLoading,
    isFormAlreadyPopulated,
    onApplicationSetEntitySelect,
    onChange,
  ]);

  const handleEntitySelect = (_: any, entity: Entity | null) => {
    if (!entity) return;
    setInaccessibleName(null);
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationSetInput(entity);
    onChange({
      applicationSetName: fullData.applicationSetName || entity.metadata.name,
      description: fullData.description || '',
      applicationSetRef: stringifyEntityRef(entity),
    });
    onApplicationSetEntitySelect(fullData);
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="ApplicationSet configuration"
          description="Select an ApplicationSet to edit its configuration."
          divider
        />
        <Backdrop open={appSetsLoading} className={classes.loadingProgress}>
          <CircularProgress size={48} />
        </Backdrop>
        <div ref={sectionRef}>
          <Box className={classes.inputContainer}>
            <ScaffolderInputHeader
              title="ApplicationSet to edit"
              description="Select the ApplicationSet you want to edit."
              infoDoc={APPLICATION_NAME_INFO}
            />
            <Autocomplete
              size="small"
              value={selectedEntity}
              loading={appSetsLoading}
              onChange={handleEntitySelect}
              options={appSetEntities}
              disableClearable
              getOptionLabel={option =>
                option.metadata.title || option.metadata.name
              }
              renderInput={params => {
                let appSetHelperText: React.ReactNode;
                if (inaccessibleName) {
                  appSetHelperText = (
                    <ScaffolderFieldError
                      message={`You do not have permission to edit ApplicationSet "${inaccessibleName}". Select a different ApplicationSet.`}
                    />
                  );
                } else if (notAccessible) {
                  appSetHelperText = (
                    <ScaffolderFieldError message="ApplicationSet is required" />
                  );
                }
                return (
                  <TextField
                    {...params}
                    label="ApplicationSet"
                    variant="outlined"
                    margin="none"
                    color="secondary"
                    required
                    InputProps={params.InputProps}
                    error={notAccessible}
                    helperText={appSetHelperText}
                  />
                );
              }}
              renderOption={option => (
                <Box>
                  <Typography variant="body2" noWrap>
                    {option.metadata.title || option.metadata.name}
                  </Typography>
                  {option.metadata.title && (
                    <Typography variant="caption" noWrap color="textSecondary">
                      {option.metadata.name}
                    </Typography>
                  )}
                </Box>
              )}
              noOptionsText={
                appSetsLoading
                  ? 'Loading ApplicationSets...'
                  : 'No accessible ApplicationSets found'
              }
              loadingText="Loading ApplicationSets..."
            />
          </Box>
        </div>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the ApplicationSet."
            infoDoc={APPLICATION_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e => onChange({ ...value, description: e.target.value })}
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            multiline
            inputProps={{ maxLength: 250 }}
            minRows={3}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ApplicationSetEditBasicInfoSection = React.memo(
  ApplicationSetEditBasicInfoSectionInner,
);
